import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { CheckCircle2, Loader2, Calendar, MapPin, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventRegistrationPage() {
  const [formData, setFormData] = useState({
    first_name: '',
    phone: '',
    email: '',
    event_id: '',
    product_interest: '',
    investment_timeline: '',
    language_preference: '',
    notes: ''
  });
  const [activeEvents, setActiveEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Fetch active events on mount
  useEffect(() => {
    const fetchActiveEvents = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/active-events`);
        if (response.ok) {
          const data = await response.json();
          setActiveEvents(data);
        }
      } catch (err) {
        logger.error('Failed to fetch active events:', err);
      } finally {
        setEventsLoading(false);
      }
    };
    fetchActiveEvents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.first_name.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.phone && !formData.email) {
      setError('Please provide either a phone number or email address');
      return false;
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.event_id) {
      setError('Please select an event to register for');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const payload = {
        first_name: formData.first_name.trim(),
        phone: formData.phone || null,
        email: formData.email || null,
        event_id: formData.event_id,
        product_interest: formData.product_interest || null,
        investment_timeline: formData.investment_timeline || null,
        language_preference: formData.language_preference || null,
        notes: formData.notes || null
      };

      const response = await fetch(`${API_URL}/api/public/event-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const data = await response.json();
        setError(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatEventDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy • h:mm a');
    } catch {
      return dateStr;
    }
  };

  const selectedEvent = activeEvents.find(e => e.event_id === formData.event_id);

  // No active events state
  if (!eventsLoading && activeEvents.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-lg">
          <CardContent className="pt-12 pb-8 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">
              No Active Events
            </h2>
            <p className="text-slate-600 text-lg">
              There are no events available for registration at this time. Please check back later.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-lg">
          <CardContent className="pt-12 pb-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">
              Registration Complete!
            </h2>
            <p className="text-slate-600 text-lg">
              Thank you for registering. Our team will contact you shortly.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-8 px-4" data-testid="event-registration-page">
      <div className="max-w-md mx-auto">
        {/* Header with Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-900 rounded-xl mb-4">
            <span className="text-gold font-heading text-2xl font-bold">G</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Goldsands Event Registration
          </h1>
          <p className="text-slate-600">
            Secure your seat at our upcoming event
          </p>
        </div>

        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-slate-900 text-white rounded-t-lg py-4">
            <h2 className="text-lg font-medium text-center">Registration Form</h2>
          </CardHeader>
          <CardContent className="p-6">
            {eventsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Event Selection - REQUIRED */}
                <div className="space-y-2">
                  <Label htmlFor="event_id" className="text-slate-700 font-medium">
                    Select Event <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.event_id}
                    onValueChange={(value) => handleChange('event_id', value)}
                  >
                    <SelectTrigger className="h-12 text-base" data-testid="select-event">
                      <SelectValue placeholder="Choose an event to register" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeEvents.map((event) => (
                        <SelectItem key={event.event_id} value={event.event_id}>
                          <div className="flex flex-col items-start py-1">
                            <span className="font-medium">{event.name}</span>
                            <span className="text-xs text-slate-500">{event.category}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Selected Event Details */}
                  {selectedEvent && (
                    <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatEventDate(selectedEvent.start_datetime)}</span>
                      </div>
                      {selectedEvent.location && (
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <MapPin className="w-4 h-4" />
                          <span>{selectedEvent.location}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* First Name */}
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-slate-700 font-medium">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    placeholder="Enter your full name"
                    className="h-12 text-base"
                    data-testid="input-first-name"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-700 font-medium">
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className="h-12 text-base"
                    data-testid="input-phone"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="you@example.com"
                    className="h-12 text-base"
                    data-testid="input-email"
                  />
                </div>

                {/* Product Interest */}
                <div className="space-y-2">
                  <Label htmlFor="product_interest" className="text-slate-700 font-medium">
                    Product Interest
                  </Label>
                  <Select
                    value={formData.product_interest}
                    onValueChange={(value) => handleChange('product_interest', value)}
                  >
                    <SelectTrigger className="h-12 text-base" data-testid="select-product-interest">
                      <SelectValue placeholder="Select your interest" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Land Plot">Land Plot</SelectItem>
                      <SelectItem value="Apartment Unit">Apartment Unit</SelectItem>
                      <SelectItem value="Hotel Share">Hotel Share</SelectItem>
                      <SelectItem value="Not Sure">Not Sure</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Investment Timeline */}
                <div className="space-y-2">
                  <Label htmlFor="investment_timeline" className="text-slate-700 font-medium">
                    Investment Timeline
                  </Label>
                  <Select
                    value={formData.investment_timeline}
                    onValueChange={(value) => handleChange('investment_timeline', value)}
                  >
                    <SelectTrigger className="h-12 text-base" data-testid="select-investment-timeline">
                      <SelectValue placeholder="When are you looking to invest?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0-30 days">0-30 days</SelectItem>
                      <SelectItem value="30-90 days">30-90 days</SelectItem>
                      <SelectItem value="90+ days">90+ days</SelectItem>
                      <SelectItem value="Not Shared">Not Shared</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Language Preference */}
                <div className="space-y-2">
                  <Label htmlFor="language_preference" className="text-slate-700 font-medium">
                    Preferred Language
                  </Label>
                  <Select
                    value={formData.language_preference}
                    onValueChange={(value) => handleChange('language_preference', value)}
                  >
                    <SelectTrigger className="h-12 text-base" data-testid="select-language">
                      <SelectValue placeholder="Select your preferred language" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bangla">Bangla</SelectItem>
                      <SelectItem value="English">English</SelectItem>
                      <SelectItem value="Mixed">Mixed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-slate-700 font-medium">
                    Additional Notes
                  </Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    placeholder="Any questions or comments?"
                    className="min-h-[80px] text-base resize-none"
                    data-testid="textarea-notes"
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm" data-testid="error-message">
                    {error}
                  </div>
                )}

                {/* Submit Button */}
                <Button 
                  type="submit" 
                  className="w-full h-12 text-base bg-gold hover:bg-gold/90 text-slate-900 font-semibold"
                  disabled={loading}
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Register Now'
                  )}
                </Button>

                <p className="text-center text-xs text-slate-500 mt-4">
                  By registering, you agree to be contacted by our team regarding investment opportunities.
                </p>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-slate-500 mt-6">
          Goldsands Group INC | Real Estate Investment
        </p>
      </div>
    </div>
  );
}
